import React, { forwardRef, useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { axios } from 'api'
import { useUserContext } from 'context'
import { Box, Flex, Image, NameList, P, ResponsiveSVG } from 'ui/acro'
import { CardSubtitle, CardTitle, TitleContainer } from '../styles'
import heartSVG from '../../../../../assets/images/heart.svg'
import heartFilledSVG from '../../../../../assets/images/heartFilled.svg'
import PlaylistNew from '../../../../../assets/images/PlaylistNew.svg'
import PlaylistFilled from '../../../../../assets/images/PlaylistFilled.svg'
import ThreeDots from '../../../../images/latest/three-dots.svg'
import TimeCodeBadge from './TimeCodeBadge'
import { storeEventInLocalStorage } from 'gtm/pushEvents'
import { LightTheme } from 'ui/bend'
import moment from 'moment'
import { AlomovesIconSVG } from 'images/latest'
import SubCategoryChipList from './SubCategoryChipList'
import AccessIdentifier from './AccessIdentifier'
import { showLabel } from './utils'
import { camelToSnake } from 'utils'
import AddToCalendarBtnForClassCard from 'components_v1/addToCalendar/AddToCalendarBtnForClassCard'
import PopoverMenu from 'components_v2/MyPractice/common/Components/PopoverMenu'
import CommonLink from 'components_v2/CommonLink'
import { useInRouterContext } from 'react-router-dom'

const HeartIconBox = styled(Box)`
  svg {
    path {
      &.heart-icon {
        fill: #3b3b3b99;
      }
    }
  }
`
const Main = styled(Flex)`
  .no-image {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f2f2f2;
    aspect-ratio: 16/9;
  }
  .class-image {
    width: 100%;
  }
`

const HeartIconComponent = ({ onClick = () => {}, isFilled = false }) => (
  <HeartIconBox
    zIndex={'1'}
    right={['5px', '6px', '6px', '4px']}
    top={['5px', '7px', '7px', '4px']}
    position={'absolute'}
    width={'32px'}
    height={'32px'}
    display={'flex'}
    borderRadius={'50%'}
    alignItems={'center'}
    justifyContent={'center'}
    onClick={onClick}
    backgroundColor={'#FFFFFF99'}
  >
    <ResponsiveSVG
      SVG={isFilled ? heartFilledSVG : heartSVG}
      style={{
        cursor: 'pointer'
      }}
      width='24px'
      height='24px'
    />
  </HeartIconBox>
)

const CompletedComponent = () => (
  <Completed>
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='12'
      height='12'
      viewBox='0 0 12 12'
      fill='none'
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M9.3 4.19977L5.4 8.39977L3 5.99977L3.47456 5.52521L5.4 7.4766L8.82632 3.72607L9.3 4.19977Z'
        fill='white'
      />
    </svg>
    <CompletedText>Completed</CompletedText>
  </Completed>
)

const ClassIndex = styled(Box)`
  width: 32px;
  background: #fff;
  color: #000;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3;
  text-align: center;
  padding: 5px;

  font-family: 'proxima-nova';
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.9px;
`

const Completed = styled.div`
  position: absolute;
  z-index: 9;
  right: 0;
  background: #000;
  display: flex;
  align-items: center;
  padding: 4px 8px;
`
const CompletedText = styled.div`
  font-family: 'Proxima-Nova';
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  text-transform: uppercase;
  color: #fff;
  margin-left: 10px;
`
const OverLey = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 8;
  background-color: rgba(0, 0, 0, 0.5);
`

const getLocalDate = (d) => {
  let date = new Date(d)
  if (moment(date).isValid()) {
    return moment(date).local().format('MM/DD/YYYY')
  }

  date = String(d).replace(/-/g, '/')
  if (moment(date).isValid()) {
    return moment(date).local().format('MM/DD/YYYY')
  }

  return String(d)
}

const isInProgramPage = (plan) => {
  if (!plan) return false

  const { slug, id } = plan
  const pathname = String(window.location.pathname).toLowerCase()

  return (
    (pathname.includes(`/series/${slug}`.toLowerCase()) ||
      pathname.includes(`/series/${id}`.toLowerCase())) &&
    (pathname.endsWith('/workouts') ||
      pathname.endsWith(slug) ||
      pathname.endsWith(String(id)))
  )
}

// this version of the class card is used on the Most Popular and New Classes HP carousels
const ClassCard = forwardRef(
  (
    {
      carouselItem,
      humanDuration,
      id,
      plan,
      videoThumbnail,
      title,
      planEntryUrl,
      favoriteCallback,
      eventName,
      position,
      location,
      isFevIcon = true,
      difficultyLevel,
      className = '',
      subcategories,
      showAllMetaData = true,
      cardIndex,
      onPlaylistIconClick,
      availabilityGating,
      membershipTier,
      isGridView,
      showScheduleButton = false,
      handleAddToCalendarClick,
      isFromMyPracticeCompleted = false,
      onRemove,
      unlocked = false,
      isProgram,
      ...rest
    },
    ref
  ) => {
    let image
    //const [isFavoritesAvailable, setIsFavoritesAvailable] = useState(rest.favorites?.length)
    const [favoritesId, setFavoritesId] = useState(
      rest?.favorites?.[0]?.id || rest?.favorite?.id
    )
    const [playlistIds, setPlaylistIds] = useState(rest?.playlistIds)
    const insideRouter = useInRouterContext()

    useEffect(() => {
      setPlaylistIds(rest?.playlistIds)
    }, [rest?.playlistIds])

    const { setSnapShot } = useUserContext() || {}
    const singleClass = Boolean(plan?.singleClass)
    const filteredSubStyles = subcategories?.filter(
      (subStyle) => subStyle.slug !== 'single-classes'
    )
    if (typeof videoThumbnail !== 'undefined') {
      image = videoThumbnail
    }

    const isProgramPage = isInProgramPage(plan)
    const isHomePage = window.location.pathname === '/'

    const programMetadataText = useMemo(() => {
      if (isFromMyPracticeCompleted && isProgram) {
        return 'Part of Program:'
      }
      if (!isFromMyPracticeCompleted) {
        return 'Program:'
      }
      return false
    }, [isFromMyPracticeCompleted, isProgram])

    const addClass = (id) => {
      setFavoritesId(id)
      return axios
        .post('/favorites', { plan_entry_id: id })
        .then((res) => {
          if (res.status === 200) {
            window.dataLayer?.push(camelToSnake(res?.data?.ga4Events))
            //class_added_my_practice event to braze
            window.braze.logCustomEvent('class_added_my_practice', {
              ...camelToSnake(res?.data?.ga4Events)
            })
            setFavoritesId(res?.data?.id || null)
            favoriteCallback?.()
            updateSnapShot()
          } else {
            setFavoritesId(null)
          }
        })
        .catch((error) => {
          setFavoritesId(null)
          console.error(error)
        })
    }

    const updateSnapShot = async () => {
      if (!isHomePage) return
      if (!setSnapShot) return
      try {
        const res = await axios.get('/my_practice/homepage_summary')
        if (res) {
          setSnapShot?.(res?.data)
        }
      } catch (e) {
        console.log('e', e)
      }
    }

    const removeClass = (id) => {
      setFavoritesId(null)
      return axios
        .delete(`/favorites/${id}`)
        .then((res) => {
          if (res.status === 200) {
            setFavoritesId(null)
            favoriteCallback?.()
            updateSnapShot()
          } else {
            setFavoritesId(id)
          }
        })
        .catch((error) => {
          console.error(error)
          setFavoritesId(id)
        })
    }

    const pushEvent = (e) => {
      if (!eventName) {
        return
      }
      const coachNames = plan?.coaches
        ?.map((item) => `${item.firstName} ${item.lastName}`)
        .join(', ')

      const event_data = {
        event: eventName,
        position,
        instructor: coachNames,
        class: title,
        ...(location && { location })
      }
      if (insideRouter) {
        window.dataLayer ||= []
        window.dataLayer.push(event_data)
        return
      }

      storeEventInLocalStorage(event_data)
    }

    const difficulty = difficultyLevel || plan?.difficultyLevel

    const heartOrcompleted =
      rest?.completed && isProgramPage ? (
        <CompletedComponent />
      ) : isFevIcon ? (
        <HeartIconComponent
          isFilled={Boolean(favoritesId)}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            !favoritesId ? addClass(id) : removeClass(favoritesId)
          }}
        />
      ) : null

    return (
      <>
        <Main
          ref={ref}
          id={id}
          flexDirection='column'
          position='relative'
          className={`${className} ${
            carouselItem ? 'swiper-slide unstyled' : 'unstyled'
          }`}
          style={{ minWidth: carouselItem ? '212px' : '173px' }}
          as={CommonLink}
          to={planEntryUrl}
          onClick={pushEvent}
        >
          {cardIndex && <ClassIndex>{cardIndex}</ClassIndex>}
          {heartOrcompleted}
          {image ? (
            <Box position='relative' mb={['8px', '10px']}>
              <Image
                src={image}
                objectFit='cover'
                position='absolute'
                top='0'
                left='0'
                style={{ aspectRatio: '16/9' }}
                className='class-image'
              />
              <TimeCodeBadge
                fontSize='12px !important'
                bottom='16px !important'
                left='16px !important'
              >
                {humanDuration}
              </TimeCodeBadge>
              {rest?.completed && isProgramPage ? <OverLey /> : ''}
              {!unlocked && showLabel(membershipTier, availabilityGating) && (
                <AccessIdentifier
                  availabilityGating={availabilityGating}
                  isGridView={isGridView}
                  bottomPosition={'16px'}
                />
              )}
            </Box>
          ) : (
            <Box mb={['8px', '10px']} className='no-image'>
              <ResponsiveSVG
                width={['68px', '68px', '130px', '130px']}
                height={['68px', '68px', '130px', '130px']}
                SVG={AlomovesIconSVG}
                className='no-thumbnail'
              />
            </Box>
          )}
          <TitleContainer style={{ alignItems: 'center' }}>
            {title && (
              <CardTitle id='SeriesTitle' as='div'>
                {title}
              </CardTitle>
            )}
            <Flex>
              {isFevIcon && (
                <HeartIconBox
                  zIndex={'1'}
                  width={'32px'}
                  height={'32px'}
                  display={'flex'}
                  borderRadius={'50%'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    onPlaylistIconClick()
                  }}
                  backgroundColor={'#FFFFFF99'}
                >
                  <ResponsiveSVG
                    SVG={playlistIds?.length ? PlaylistFilled : PlaylistNew}
                    style={{
                      cursor: 'pointer'
                    }}
                    width='24px'
                    height='24px'
                  />
                </HeartIconBox>
              )}

              {isFromMyPracticeCompleted && (
                <PopoverMenu
                  trigger={
                    <ResponsiveSVG SVG={ThreeDots} width='24px' height='24px' />
                  }
                  options={[
                    {
                      label: 'Remove from Completed',
                      onClick: (e) => {
                        onRemove()
                      }
                    }
                  ]}
                />
              )}
            </Flex>
          </TitleContainer>

          {plan?.coaches && (
            <CardSubtitle as='div'>
              <NameList people={plan.coaches} />
            </CardSubtitle>
          )}
          <CardSubtitle as='div' marginTop='4px'>
            {difficulty && `${difficulty}`}
          </CardSubtitle>
          <CardSubtitle
            color={`${LightTheme.colors.grey5} !important`}
            as='div'
            marginTop='4px'
            lineHeight='18px !important'
          >
            {rest?.completedAt && !isProgramPage ? (
              <P fontSize='14px' color='#758E6D' margin='0'>
                Completed: {getLocalDate(rest?.completedAt)}
              </P>
            ) : null}
            {!singleClass && showAllMetaData && programMetadataText && (
              <>
                <span
                  {...(isFromMyPracticeCompleted && {
                    style: {
                      textTransform: 'none'
                    }
                  })}
                >
                  {programMetadataText}{' '}
                </span>
                {plan?.slug ? (
                  <CommonLink
                    style={{
                      textDecoration: 'underline',
                      color: '#636363'
                    }}
                    to={`/series/${plan?.slug}`}
                  >
                    {plan?.title}
                  </CommonLink>
                ) : (
                  <a
                    style={{
                      textDecoration: 'underline',
                      color: '#636363'
                    }}
                    {...(plan?.slug ? { href: `/series/${plan?.slug}` } : {})}
                  >
                    {plan?.title}
                  </a>
                )}
              </>
            )}
          </CardSubtitle>
          {filteredSubStyles && (
            <SubCategoryChipList
              subcategories={filteredSubStyles}
              isGridView={isGridView}
            />
          )}
          {showScheduleButton && (
            <AddToCalendarBtnForClassCard
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                handleAddToCalendarClick()
              }}
            />
          )}
        </Main>
      </>
    )
  }
)

ClassCard.propTypes = {
  duration: PropTypes.string,
  id: PropTypes.number,
  plan: PropTypes.object,
  title: PropTypes.string.isRequired,
  videoThumbnail: PropTypes.string.isRequired,
  planEntryUrl: PropTypes.string.isRequired
}

export default ClassCard
